import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft, Settings, Cookie, Trash2, Download, Mail, Check } from 'lucide-react';
import { toast } from 'sonner';

const GestionePrivacyPage = () => {
  const navigate = useNavigate();
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Load current cookie preferences
    const accepted = localStorage.getItem('cookiesAccepted');
    if (accepted === 'true') {
      setCookiePreferences({
        necessary: true,
        analytics: true,
        marketing: true
      });
    } else if (accepted === 'necessary') {
      setCookiePreferences({
        necessary: true,
        analytics: false,
        marketing: false
      });
    }
  }, []);

  const saveCookiePreferences = () => {
    if (cookiePreferences.analytics || cookiePreferences.marketing) {
      localStorage.setItem('cookiesAccepted', 'true');
    } else {
      localStorage.setItem('cookiesAccepted', 'necessary');
    }
    localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences));
    toast.success('Preferenze cookie salvate');
  };

  const handleDeleteAccount = () => {
    toast.info('Per eliminare il tuo account, contattaci attraverso la pagina Contatti');
  };

  const handleDataExport = () => {
    toast.info('Per richiedere l\'esportazione dei tuoi dati, contattaci attraverso la pagina Contatti');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <nav className="bg-white border-b border-slate-200 py-4">
        <div className="max-w-4xl mx-auto px-6">
          <Button
            onClick={() => navigate(-1)}
            variant="ghost"
            className="text-slate-600 hover:text-[#344B71]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Indietro
          </Button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-gradient-to-br from-[#344B71] to-[#344B71] rounded-full flex items-center justify-center mx-auto mb-6">
              <Settings className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-playfair font-bold text-[#344B71] mb-4">
              Gestione Privacy
            </h1>
            <p className="text-slate-600">
              Gestisci le tue preferenze sulla privacy e i tuoi dati personali
            </p>
          </div>

          {/* Content */}
          <div className="space-y-8">
            {/* Cookie Preferences */}
            <Card className="p-8 bg-white">
              <div className="flex items-center space-x-3 mb-6">
                <Cookie className="w-6 h-6 text-[#344B71]" />
                <h2 className="text-xl font-semibold text-[#344B71]">Preferenze Cookie</h2>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div>
                    <h3 className="font-medium text-[#344B71]">Cookie Necessari</h3>
                    <p className="text-sm text-slate-600">Essenziali per il funzionamento del sito</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Check className="w-5 h-5 text-emerald-600" />
                    <span className="text-sm text-slate-600">Sempre attivi</span>
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div>
                    <h3 className="font-medium text-[#344B71]">Cookie Analitici</h3>
                    <p className="text-sm text-slate-600">Ci aiutano a capire come utilizzi il sito</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={cookiePreferences.analytics}
                      onChange={(e) => setCookiePreferences({...cookiePreferences, analytics: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#344B71]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#344B71]"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                  <div>
                    <h3 className="font-medium text-[#344B71]">Cookie di Marketing</h3>
                    <p className="text-sm text-slate-600">Utilizzati per mostrarti contenuti personalizzati</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={cookiePreferences.marketing}
                      onChange={(e) => setCookiePreferences({...cookiePreferences, marketing: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#344B71]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#344B71]"></div>
                  </label>
                </div>

                <Button
                  onClick={saveCookiePreferences}
                  className="w-full bg-[#344B71] hover:bg-[#344B71] text-white mt-4 rounded-xl"
                >
                  Salva Preferenze Cookie
                </Button>
              </div>
            </Card>

            {/* Data Rights */}
            <Card className="p-8 bg-white">
              <div className="flex items-center space-x-3 mb-6">
                <Download className="w-6 h-6 text-[#344B71]" />
                <h2 className="text-xl font-semibold text-[#344B71]">I Tuoi Dati</h2>
              </div>
              
              <div className="space-y-4">
                <div className="p-4 bg-slate-50 rounded-xl">
                  <h3 className="font-medium text-[#344B71] mb-2">Esporta i tuoi dati</h3>
                  <p className="text-sm text-slate-600 mb-4">
                    Richiedi una copia di tutti i dati personali che abbiamo raccolto su di te.
                  </p>
                  <Button
                    onClick={handleDataExport}
                    variant="outline"
                    className="border-[#344B71] text-[#344B71] hover:bg-[#344B71] hover:text-white rounded-xl"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Richiedi Esportazione Dati
                  </Button>
                </div>

                <div className="p-4 bg-red-50 rounded-xl border border-red-100">
                  <h3 className="font-medium text-red-900 mb-2">Elimina il tuo account</h3>
                  <p className="text-sm text-red-700 mb-4">
                    Elimina permanentemente il tuo account e tutti i dati associati. Questa azione è irreversibile.
                  </p>
                  <Button
                    onClick={handleDeleteAccount}
                    variant="outline"
                    className="border-red-300 text-red-700 hover:bg-red-100 rounded-xl"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Richiedi Eliminazione Account
                  </Button>
                </div>
              </div>
            </Card>

            {/* Contact for Privacy */}
            <Card className="p-8 bg-white">
              <div className="flex items-center space-x-3 mb-6">
                <Mail className="w-6 h-6 text-[#344B71]" />
                <h2 className="text-xl font-semibold text-[#344B71]">Contattaci per la Privacy</h2>
              </div>
              
              <p className="text-slate-600 mb-4">
                Per qualsiasi domanda o richiesta relativa alla privacy e al trattamento dei tuoi dati 
                personali, puoi contattarci attraverso la nostra pagina di contatto.
              </p>
              
              <Button
                onClick={() => navigate('/contatti')}
                className="bg-[#344B71] hover:bg-[#344B71] text-white rounded-xl"
              >
                Vai alla Pagina Contatti
              </Button>
            </Card>

            {/* Link to Privacy Policy */}
            <div className="text-center">
              <a
                href="/privacy"
                className="text-slate-600 hover:text-[#344B71] underline transition-colors"
              >
                Leggi la Privacy Policy completa
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default GestionePrivacyPage;
